/**
 * Frontend Entry Point
 * React app initialization with Router, Auth, and React Query providers
 */

import './index.css';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, Outlet, Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
// MapLibre GL JS is loaded from CDN (sets window.maplibregl).
// No access token needed for library init — token is only used in mapStyles.ts
// for Mapbox-hosted fonts/sprites (interim, until self-hosted alternatives).

// Providers
import { AuthProvider, useAuth } from './hooks/useAuth';
import { isV3 } from './hooks/useDesignVersion';
import { FlagProvider } from './contexts/FlagContext';
import { ToastProvider, ToastContainer } from './components/Toast';
import { OnlineStatusProvider } from './contexts/OnlineStatusContext';
import { DemoRoleProvider, useDemoRole } from './contexts/DemoRoleContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { PanelProvider, usePanels } from './contexts/PanelContext';
import { UIProvider, bacchusTheme } from '@00start/ui';
import { UpgradeModal } from './components/UpgradeModal';

// Demo Role Components
import { ReadOnlyBanner } from './components/ReadOnlyBanner';

// Lazy-loaded Pages (code-split by route via registry)
import {
  LoginPage,
  ForgotPasswordPage,
  DashboardPage,
  ExceptionQueuePage,
  MobileUploadPage,
  PolicyDetailPage,
  FindingsListPage,
  VARPage,
  SecureLinksPage,
  AdminIntegrationsPage,
  AdminUsersPage,
  AdminHealthPage,
  AdminLogsPage,
  AdminKPIPage,
  ActivationPage,
  CreateSubjectivityPage,
  AdminUserDetailPage,
  AdminRolesPage,
  AdminPartnersPage,
  CreatePolicyPage,
  EditPolicyPage,
  InspectionsListPage,
  InspectionDetailPage,
  InspectionJobsPage,
  CreateFindingPage,
  FindingDetailPage,
  EditFindingPage,
  PhotoUploadPage,
  PhotoDetailPage,
  PhotoEditPage,
  RenewalTrackerPage,
  RenewalDetailPage,
  StructureDetailPage,
  EvidencePacketPage,
  AdminAlertsPage,
  AdminIndexPage,
  AdminBackfillThumbnailsPage,
  AdminFlagsPage,
  ImportPortfolioPage,
  BuildingsListPage,
  BuildingDetailPage,
  BuildingEditorPage,
  PoliciesListPage,
  PhotoSearchPage,
  PortfolioAnalyticsPage,
  SubjectivitiesListPage,
  SubjectivityDetailPage,
  AIAnalysisPage,
  DocumentSearchPage,
  InspectorDashboardPage,
  UnderwriterReviewPage,
  BrokerReviewPage,
  BrokerReportDetailPage,
  BrokerEvidencePacketPage,
  VARSharedViewPage,
  MoreMenuPage,
  UserSettingsPage,
  NotificationSettingsPage,
  SignupPage,
  AdminPartnerDomainsPage,
  AdminPendingInvitesPage,
  TermsPage,
  PrivacyPage,
  SecurityPage,
  ClaimsListPage,
  ClaimDetailPage,
} from './registry/pages';
import { SuspenseBoundary } from './registry/SuspenseBoundary';
import { FlagGate } from './components/FlagGate';

// Navigation
import { GlobalNav } from './components/navigation/GlobalNav';
import { MobileTabBar } from './components/navigation/MobileTabBar';
import { AppSidebar } from './components/navigation/AppSidebar';
import { RightTray } from './components/RightTray';
import { useIsMobile } from './hooks/useIsMobile';
import { SidebarProvider } from './contexts/SidebarContext';

// Components
import { Spinner, PWAInstallPrompt } from './components';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AppFooter } from './components/AppFooter';

// ============================================================================
// Query Client Configuration
// ============================================================================

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

// ============================================================================
// Protected Route Component
// ============================================================================

function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="xl" label="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

// ============================================================================
// Admin Route Guard Component
// ============================================================================

function AdminRoute() {
  const { user } = useAuth();

  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}

// ============================================================================
// Public Route Component (redirect if already logged in)
// ============================================================================

function PublicRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="xl" label="Loading..." />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}

// ============================================================================
// App Layout with Navigation
// ============================================================================

function AppLayout() {
  const { isReadOnly } = useDemoRole();
  const { rightVisible, bottomVisible, toggleRight } = usePanels();
  const isMobile = useIsMobile();
  // v2: 56px tab bar on mobile only (pb-14 md:pb-0), Inter font
  // v3: 40px mobile / 34px desktop bottom sheet tabs, system-ui font (§4.3)
  //     When bottom tabs are hidden (toggle off), no padding needed.
  const bottomPaddingClass = isV3()
    ? (bottomVisible ? 'pb-10 xl:pb-[34px]' : '')
    : 'pb-14 md:pb-0';
  const fontClass = isV3() ? 'font-system' : '';

  // v3: canvas gradient on root wrapper, transparent zones
  // v2: flat bg-gray-50 / bg-slate-950
  const shellClass = isV3()
    ? `min-h-screen ${bottomPaddingClass} ${fontClass} flex`
    : `min-h-screen bg-gray-50 dark:bg-slate-950 ${bottomPaddingClass} flex`;

  const content = (
    <SidebarProvider>
      <div className={shellClass}>
        {/* Desktop Sidebar (lg+) */}
        <AppSidebar />

        {/* Main content area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top Nav (md-lg tablet, <md shows hamburger only, lg+ hidden) */}
          <GlobalNav />

          {/* Read-only banner for underwriter demo mode */}
          <ReadOnlyBanner visible={isReadOnly} />

          {/* Page Content */}
          <div className="flex-1">
            <Outlet />
          </div>

          {/* Footer (desktop only — mobile has tab bar) */}
          <AppFooter />

          {/* Mobile bottom tab navigation (1-tap access to top destinations) */}
          <MobileTabBar />
        </div>
        {/* Right tray — v3 only, controlled by PanelContext */}
        {isV3() && rightVisible && (
          <RightTray isMobile={isMobile} onClose={isMobile ? toggleRight : undefined} />
        )}
      </div>
    </SidebarProvider>
  );

  // v3: wrap in canvas gradient — applied to React root wrapper, not <html>
  // (avoids leaking to browser extensions/overlays outside React tree)
  if (isV3()) {
    return <div className="canvas-gradient">{content}</div>;
  }
  return content;
}

// ============================================================================
// Admin Layout with Sidebar
// ============================================================================

function AdminLayout() {
  return (
    <>
      {/* Mobile/tablet back link to admin index (sidebar handles admin nav at lg+) */}
      <div className="lg:hidden px-4 py-3 border-b border-gray-200 bg-white dark:bg-slate-900 dark:border-slate-800">
        <Link to="/admin" className="flex items-center gap-1 text-sm text-gray-600 dark:text-slate-400 font-medium">
          <ChevronLeft className="h-4 w-4" /> Admin
        </Link>
      </div>
      <Outlet />
    </>
  );
}

// ============================================================================
// App Routes
// ============================================================================

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route element={<PublicRoute />}>
        <Route path="/login" element={<SuspenseBoundary><LoginPage /></SuspenseBoundary>} />
        <Route path="/forgot-password" element={<SuspenseBoundary><ForgotPasswordPage /></SuspenseBoundary>} />
      </Route>

      {/* Mobile Upload (Public - uses secure link UUID) */}
      <Route path="/upload/:uuid" element={<SuspenseBoundary><MobileUploadPage /></SuspenseBoundary>} />

      {/* Account Activation (Public - uses invite token) */}
      <Route path="/activate/:token" element={<SuspenseBoundary><ActivationPage /></SuspenseBoundary>} />
      {/* Signup (gated viral identity - magic link landing) */}
      <Route path="/signup/:token" element={<SuspenseBoundary><SignupPage /></SuspenseBoundary>} />

      {/* VAR Shared View (Public - uses share token, no auth required) */}
      <Route path="/var/shared/:token" element={<SuspenseBoundary><VARSharedViewPage /></SuspenseBoundary>} />

      {/* Legal Pages (Public - no auth required) */}
      <Route path="/terms" element={<SuspenseBoundary><TermsPage /></SuspenseBoundary>} />
      <Route path="/privacy" element={<SuspenseBoundary><PrivacyPage /></SuspenseBoundary>} />
      <Route path="/security" element={<SuspenseBoundary><SecurityPage /></SuspenseBoundary>} />

      {/* Protected Routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route path="/dashboard" element={<SuspenseBoundary><DashboardPage /></SuspenseBoundary>} />

          {/* Policies */}
          <Route path="/policies" element={<SuspenseBoundary><PoliciesListPage /></SuspenseBoundary>} />
          <Route path="/policies/new" element={<SuspenseBoundary><CreatePolicyPage /></SuspenseBoundary>} />
          <Route path="/policies/:id/edit" element={<SuspenseBoundary><EditPolicyPage /></SuspenseBoundary>} />
          <Route path="/policies/:id" element={<SuspenseBoundary><PolicyDetailPage /></SuspenseBoundary>} />

          {/* Claims */}
          <Route path="/claims" element={<SuspenseBoundary><ClaimsListPage /></SuspenseBoundary>} />
          <Route path="/claims/:id" element={<SuspenseBoundary><ClaimDetailPage /></SuspenseBoundary>} />

          {/* Buildings */}
          <Route path="/buildings" element={<SuspenseBoundary><BuildingsListPage /></SuspenseBoundary>} />
          <Route path="/buildings/new" element={<SuspenseBoundary><BuildingEditorPage /></SuspenseBoundary>} />
          <Route path="/buildings/:id/edit" element={<SuspenseBoundary><BuildingEditorPage /></SuspenseBoundary>} />
          <Route path="/buildings/:id" element={<SuspenseBoundary><BuildingDetailPage /></SuspenseBoundary>} />

          {/* Findings */}
          <Route path="/findings" element={<SuspenseBoundary><FindingsListPage /></SuspenseBoundary>} />
          <Route path="/findings/new" element={<SuspenseBoundary><CreateFindingPage /></SuspenseBoundary>} />
          <Route path="/findings/:id/edit" element={<SuspenseBoundary><EditFindingPage /></SuspenseBoundary>} />
          <Route path="/findings/:id" element={<SuspenseBoundary><FindingDetailPage /></SuspenseBoundary>} />

          {/* Inspections */}
          <Route path="/inspections" element={<SuspenseBoundary><InspectionsListPage /></SuspenseBoundary>} />
          <Route path="/inspections/:id" element={<SuspenseBoundary><InspectionDetailPage /></SuspenseBoundary>} />
          <Route path="/inspection-jobs" element={<SuspenseBoundary><InspectionJobsPage /></SuspenseBoundary>} />
          <Route path="/inspector-dashboard" element={<SuspenseBoundary><InspectorDashboardPage /></SuspenseBoundary>} />
          <Route path="/secure-links" element={<SuspenseBoundary><SecureLinksPage /></SuspenseBoundary>} />

          {/* Photos */}
          <Route path="/photos" element={<SuspenseBoundary><PhotoSearchPage /></SuspenseBoundary>} />
          <Route path="/photos/upload" element={<SuspenseBoundary><PhotoUploadPage /></SuspenseBoundary>} />
          <Route path="/photos/:id/edit" element={<SuspenseBoundary><PhotoEditPage /></SuspenseBoundary>} />
          <Route path="/photos/:id" element={<SuspenseBoundary><PhotoDetailPage /></SuspenseBoundary>} />

          {/* Subjectivities */}
          <Route path="/subjectivities" element={<SuspenseBoundary><SubjectivitiesListPage /></SuspenseBoundary>} />
          <Route path="/subjectivities/create" element={<SuspenseBoundary><CreateSubjectivityPage /></SuspenseBoundary>} />
          <Route path="/subjectivities/:id" element={<SuspenseBoundary><SubjectivityDetailPage /></SuspenseBoundary>} />

          <Route path="/exceptions" element={<SuspenseBoundary><ExceptionQueuePage /></SuspenseBoundary>} />
          <Route path="/var" element={<SuspenseBoundary><FlagGate flag="commercial.var_dashboard"><VARPage /></FlagGate></SuspenseBoundary>} />
          <Route path="/documents" element={<SuspenseBoundary><DocumentSearchPage /></SuspenseBoundary>} />
          <Route path="/portfolio-analytics" element={<SuspenseBoundary><FlagGate flag="commercial.portfolio_analytics"><PortfolioAnalyticsPage /></FlagGate></SuspenseBoundary>} />

          {/* Broker Routes */}
          <Route path="/renewals" element={<SuspenseBoundary><RenewalTrackerPage /></SuspenseBoundary>} />
          <Route path="/renewals/:id" element={<ErrorBoundary><SuspenseBoundary><RenewalDetailPage /></SuspenseBoundary></ErrorBoundary>} />
          <Route path="/renewals/:id/structures/:structureId" element={<ErrorBoundary><SuspenseBoundary><StructureDetailPage /></SuspenseBoundary></ErrorBoundary>} />
          <Route path="/renewals/:id/evidence-packet" element={<ErrorBoundary><SuspenseBoundary><EvidencePacketPage /></SuspenseBoundary></ErrorBoundary>} />
          <Route path="/uw-review" element={<SuspenseBoundary><UnderwriterReviewPage /></SuspenseBoundary>} />

          {/* Broker Report (cloned from underwriter/evidence workflow) */}
          <Route path="/broker-report" element={<SuspenseBoundary><BrokerReviewPage /></SuspenseBoundary>} />
          <Route path="/broker-report/:id" element={<ErrorBoundary><SuspenseBoundary><BrokerReportDetailPage /></SuspenseBoundary></ErrorBoundary>} />
          <Route path="/broker-report/:id/evidence-packet" element={<ErrorBoundary><SuspenseBoundary><BrokerEvidencePacketPage /></SuspenseBoundary></ErrorBoundary>} />
          <Route path="/import" element={<SuspenseBoundary><ImportPortfolioPage /></SuspenseBoundary>} />

          {/* AI Analysis (Preview) */}
          <Route path="/ai-analysis" element={<SuspenseBoundary><FlagGate flag="commercial.ai_sidebar"><AIAnalysisPage /></FlagGate></SuspenseBoundary>} />

          {/* More Menu (full-screen, replaces MobileDrawer) */}
          <Route path="/more" element={<SuspenseBoundary><MoreMenuPage /></SuspenseBoundary>} />

          {/* User Settings & Notification Preferences */}
          <Route path="/settings" element={<SuspenseBoundary><UserSettingsPage /></SuspenseBoundary>} />
          <Route path="/settings/notifications" element={<SuspenseBoundary><NotificationSettingsPage /></SuspenseBoundary>} />

          {/* Admin Console Routes - requires admin role */}
          <Route element={<AdminRoute />}>
            <Route element={<AdminLayout />}>
              <Route path="/admin/integrations" element={<SuspenseBoundary><AdminIntegrationsPage /></SuspenseBoundary>} />
              <Route path="/admin/users" element={<SuspenseBoundary><AdminUsersPage /></SuspenseBoundary>} />
              <Route path="/admin/users/:id" element={<SuspenseBoundary><AdminUserDetailPage /></SuspenseBoundary>} />
              <Route path="/admin/roles" element={<SuspenseBoundary><AdminRolesPage /></SuspenseBoundary>} />
              <Route path="/admin/partners" element={<SuspenseBoundary><AdminPartnersPage /></SuspenseBoundary>} />
              <Route path="/admin/health" element={<SuspenseBoundary><AdminHealthPage /></SuspenseBoundary>} />
              <Route path="/admin/logs" element={<SuspenseBoundary><AdminLogsPage /></SuspenseBoundary>} />
              <Route path="/admin/kpis" element={<SuspenseBoundary><AdminKPIPage /></SuspenseBoundary>} />
              <Route path="/admin/alerts" element={<SuspenseBoundary><AdminAlertsPage /></SuspenseBoundary>} />
              <Route path="/admin/backfill-thumbnails" element={<SuspenseBoundary><AdminBackfillThumbnailsPage /></SuspenseBoundary>} />
              <Route path="/admin/flags" element={<SuspenseBoundary><AdminFlagsPage /></SuspenseBoundary>} />
              <Route path="/admin/partner-domains" element={<SuspenseBoundary><AdminPartnerDomainsPage /></SuspenseBoundary>} />
              <Route path="/admin/pending-invites" element={<SuspenseBoundary><AdminPendingInvitesPage /></SuspenseBoundary>} />
            </Route>
            <Route path="/admin" element={<SuspenseBoundary><AdminIndexPage /></SuspenseBoundary>} />
          </Route>
        </Route>
      </Route>

      {/* Redirect root to dashboard */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* 404 Page */}
      <Route
        path="*"
        element={
          <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950">
            <div className="text-center">
              <h1 className="text-6xl font-bold text-gray-900 dark:text-slate-100">404</h1>
              <p className="mt-4 text-xl text-gray-600 dark:text-slate-400">Page not found</p>
              <a
                href="/dashboard"
                className="mt-6 inline-block text-gray-700 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-100"
              >
                Go to Dashboard
              </a>
            </div>
          </div>
        }
      />
    </Routes>
  );
}

// ============================================================================
// Main App Component
// ============================================================================

function App() {
  return (
    <StrictMode>
      <UIProvider theme={bacchusTheme}>
        <ThemeProvider>
          <ErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <FlagProvider>
            <AuthProvider>
              <SubscriptionProvider>
                <ToastProvider>
                  <OnlineStatusProvider>
                    <DemoRoleProvider>
                      <BrowserRouter>
                        <PanelProvider>
                          <AppRoutes />
                        </PanelProvider>
                        <ToastContainer position="top-right" />
                        {/* PWA Install Prompt - shows after 30 seconds if app is installable */}
                        <PWAInstallPrompt />
                        {/* Global Upgrade Modal — triggered from any tier-gated component */}
                        <UpgradeModal />
                      </BrowserRouter>
                    </DemoRoleProvider>
                  </OnlineStatusProvider>
                </ToastProvider>
              </SubscriptionProvider>
            </AuthProvider>
            </FlagProvider>
            {/* React Query DevTools - only in development */}
            {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
          </QueryClientProvider>
          </ErrorBoundary>
        </ThemeProvider>
      </UIProvider>
    </StrictMode>
  );
}

// ============================================================================
// Mount App
// ============================================================================

const container = document.getElementById('root');

if (container) {
  const root = createRoot(container);
  root.render(<App />);
} else {
  if (import.meta.env.DEV) console.error('Root element not found');
}

export default App;
